import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { LoadingSteps } from '../../components/shared';
import { track, EVENTS } from '../../utils/analytics';
import type { LoadingStep } from '../../types';

const QUIZ_LOADING_STEPS: LoadingStep[] = [
  { id: 'analyze', label: 'Analyzing your financial profile...', status: 'pending' },
  { id: 'generate', label: 'Finding the best strategies for you...', status: 'pending' },
  { id: 'optimize', label: 'Crafting your personalized blueprint...', status: 'pending' },
  { id: 'finalize', label: 'Finalizing your financial blueprint...', status: 'pending' },
];

const EXPRESS_LOADING_STEPS: LoadingStep[] = [
  { id: 'analyze', label: 'Reading your financial situation...', status: 'pending' },
  { id: 'generate', label: 'Mapping accounts, rules, and automations...', status: 'pending' },
  { id: 'optimize', label: 'Building your personalized setup...', status: 'pending' },
  { id: 'finalize', label: 'Finalizing your financial setup...', status: 'pending' },
];

const STEP_DURATION = 3000;
const LAST_STEP_INDEX = QUIZ_LOADING_STEPS.length - 1;

export default function LoadingScreen() {
  const navigate = useNavigate();
  const { state, setApiResult, setApiError, setIncomeBracket, setPlaygroundId } = useApp();
  const { api, flow } = state;
  const isExpress = flow === 'express';
  const [steps, setSteps] = useState<LoadingStep[]>(isExpress ? EXPRESS_LOADING_STEPS : QUIZ_LOADING_STEPS);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [apiComplete, setApiComplete] = useState(false);
  const apiResultRef = useRef<{ playgroundId: string } | null>(null);
  const hasStartedRef = useRef(false);

  // Redirect to quiz if no API request or not in loading state
  useEffect(() => {
    if (api.status === 'idle' || !api.request) {
      navigate('/quiz', { replace: true });
    }
  }, [api.status, api.request, navigate]);

  // Animate through steps 0-2 on a fixed 3s timer, stop at step 3
  useEffect(() => {
    if (api.status !== 'loading') return;

    const interval = setInterval(() => {
      setCurrentStepIndex((prev) => {
        if (prev < LAST_STEP_INDEX) {
          return prev + 1;
        }
        return prev;
      });
    }, STEP_DURATION);

    return () => clearInterval(interval);
  }, [api.status]);

  // Update step statuses based on current index
  useEffect(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step, index) => ({
        ...step,
        status:
          index < currentStepIndex
            ? 'completed'
            : index === currentStepIndex
            ? 'active'
            : 'pending',
      }))
    );
  }, [currentStepIndex]);

  // Navigate when both conditions met: reached last step AND API done
  const handleNavigation = useCallback(() => {
    if (currentStepIndex >= LAST_STEP_INDEX && apiComplete && apiResultRef.current) {
      const { playgroundId } = apiResultRef.current;
      setTimeout(() => {
        navigate(`/results/${playgroundId}`, { replace: true });
      }, 500);
    }
  }, [currentStepIndex, apiComplete, navigate]);

  useEffect(() => {
    handleNavigation();
  }, [handleNavigation]);

  // Make API calls: generate-map then create-playground
  useEffect(() => {
    if (api.status !== 'loading' || !api.request || hasStartedRef.current) return;

    hasStartedRef.current = true;

    const makeApiCalls = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';

        // Step 1: Generate the map
        const mapResponse = await fetch(`${apiUrl}/api/generate-map`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(api.request),
        });

        if (!mapResponse.ok) {
          if (mapResponse.status === 422) {
            const errorData = await mapResponse.json();
            setApiError(errorData.detail);
            setTimeout(() => {
              navigate(isExpress ? '/express' : '/quiz', { replace: true });
            }, 4000);
            return;
          }
          throw new Error(`API error: ${mapResponse.status}`);
        }

        const mapResult = await mapResponse.json();
        track(EVENTS.PLAN_GENERATED, { flow });

        if (mapResult.incomeBracket) {
          setIncomeBracket(mapResult.incomeBracket);
        }
        setApiResult(mapResult);

        // Step 2: Create the playground so the iframe is ready
        const playgroundResponse = await fetch(`${apiUrl}/api/create-playground`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            map: mapResult.map,
            name: mapResult.map?.name || (isExpress ? 'My Financial Setup' : 'My Financial Blueprint'),
          }),
        });

        if (!playgroundResponse.ok) {
          throw new Error(`Playground API error: ${playgroundResponse.status}`);
        }

        const playgroundData = await playgroundResponse.json();
        track(EVENTS.PLAYGROUND_CREATED, { flow, playgroundId: playgroundData.id });
        setPlaygroundId(playgroundData.id);

        apiResultRef.current = { playgroundId: playgroundData.id };
        setApiComplete(true);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An error occurred';
        track(EVENTS.PLAN_GENERATION_FAILED, { flow, error: errorMessage });
        setApiError(errorMessage);

        // Wait a moment then redirect back
        setTimeout(() => {
          navigate('/quiz', { replace: true });
        }, 3000);
      }
    };

    makeApiCalls();
  }, [api.status, api.request, setApiResult, setApiError, setIncomeBracket, setPlaygroundId, navigate]);

  // Handle error display
  if (api.status === 'error') {
    return (
      <div className="loading-page">
        <div className="loading-page__container">
          <div className="loading-page__header">
            <h1 className="loading-page__title">Something went wrong</h1>
            <p className="loading-page__subtitle">
              {api.error || 'Please try again'}
            </p>
          </div>
          <p style={{ color: 'var(--color-text-tertiary)', marginTop: 'var(--space-4)' }}>
            Redirecting you back...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="loading-page">
      <div className="loading-page__container">
        <div className="loading-page__header">
          <h1 className="loading-page__title">
            {isExpress ? 'Building Your Financial Setup' : 'Building Your Personalized Blueprint'}
          </h1>
          <p className="loading-page__subtitle">
            Tailoring every detail to your goals and financial situation
          </p>
        </div>

        <LoadingSteps steps={steps} />
      </div>
    </div>
  );
}
